class CartRemoveButton extends HTMLElement {
  constructor() {
    super();

    this.addEventListener("click", (event) => {
      event.preventDefault();
      const cartItems =
        this.closest("cart-items") || this.closest("cart-drawer-items");

      cartItems.updateQuantity(this.dataset.index, 0);
    });
  }
}

customElements.define("cart-remove-button", CartRemoveButton);

class CartItems extends HTMLElement {
  cartUpdateUnsubscriber = undefined;
  constructor() {
    super();
    this.lineItemStatusElement =
      document.getElementById("shopping-cart-line-item-status") ||
      document.getElementById("CartDrawer-LineItemStatus");

    const debouncedOnChange = debounce((event) => {
      this.onChange(event);
    }, ON_CHANGE_DEBOUNCE_TIMER);

    this.addEventListener("change", debouncedOnChange.bind(this));
  }

  connectedCallback() {
    this.cartUpdateUnsubscriber = subscribe(
      PUB_SUB_EVENTS.cartUpdate,
      (event) => {
        if (event.source === "cart-items") {
          return;
        }
        this.onCartUpdate();
      },
    );
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
  }

  onChange(event) {
    this.updateQuantity(
      event.target.dataset.index,
      event.target.value,
      document.activeElement.getAttribute("name"),
    );
  }

  onCartUpdate() {
    fetch(`${routes.cart_url}?section_id=main-cart-items`)
      .then((response) => response.text())
      .then((responseText) => {
        const html = new DOMParser().parseFromString(responseText, "text/html");
        const sourceQty = html.querySelector("cart-items");
        this.innerHTML = sourceQty.innerHTML;
      })
      .catch((e) => {
        console.error(e);
      });
  }

  getSectionsToRender() {
    return [
      {
        id: "main-cart-items",
        section: document.getElementById("main-cart-items").dataset.id,
        selector: ".js-contents",
      },
      {
        id: "cart-icon-bubble",
        section: "cart-icon-bubble",
        selector: ".shopify-section",
      },
      {
        id: "cart-live-region-text",
        section: "cart-live-region-text",
        selector: ".shopify-section",
      },
      {
        id: "main-cart-footer",
        section: document.getElementById("main-cart-footer").dataset.id,
        selector: ".js-contents",
      },
    ];
  }

  updateQuantity(line, quantity, name) {
    this.enableLoading(line);

    const body = JSON.stringify({
      line,
      quantity,
      sections: this.getSectionsToRender().map((section) => section.section),
      sections_url: window.location.pathname,
    });

    fetch(`${routes.cart_change_url}`, { ...fetchConfig(), ...{ body } })
      .then((response) => {
        return response.text();
      })
      .then((state) => {
        const parsedState = JSON.parse(state);
        const quantityElement =
          document.getElementById(`Quantity-${line}`) ||
          document.getElementById(`Drawer-quantity-${line}`);
        const items = document.querySelectorAll(".cart-item");

        if (parsedState.errors) {
          quantityElement.value = quantityElement.getAttribute("value");
          this.updateLiveRegions(line, parsedState.errors);
          return;
        }

        this.classList.toggle("is-empty", parsedState.item_count === 0);
        const cartDrawerWrapper = document.querySelector("cart-drawer");
        const cartFooter = document.getElementById("main-cart-footer");

        if (cartFooter)
          cartFooter.classList.toggle("is-empty", parsedState.item_count === 0);
        if (cartDrawerWrapper)
          cartDrawerWrapper.classList.toggle(
            "is-empty",
            parsedState.item_count === 0,
          );

        this.getSectionsToRender().forEach((section) => {
          const elementToReplace =
            document
              .getElementById(section.id)
              .querySelector(section.selector) ||
            document.getElementById(section.id);
          elementToReplace.innerHTML =
            this.getSectionInnerHTML(
              parsedState.sections[section.section],
              section.selector,
            ) || "";
        });
        const updatedValue = parsedState.items[line - 1]
          ? parsedState.items[line - 1].quantity
          : undefined;
        let message = "";
        if (
          items.length === parsedState.items.length &&
          updatedValue !== parseInt(quantityElement.value)
        ) {
          if (typeof updatedValue === "undefined") {
            message = window.cartStrings.error;
          } else {
            message = window.cartStrings.quantityError.replace(
              "[quantity]",
              updatedValue,
            );
          }
        }
        this.updateLiveRegions(line, message);

        const lineItem =
          document.getElementById(`CartItem-${line}`) ||
          document.getElementById(`CartDrawer-Item-${line}`);
        if (lineItem && lineItem.querySelector(`[name="${name}"]`)) {
          cartDrawerWrapper
            ? trapFocus(
                cartDrawerWrapper,
                lineItem.querySelector(`[name="${name}"]`),
              )
            : lineItem.querySelector(`[name="${name}"]`).focus();
        } else if (document.querySelector(".cart-item") && cartDrawerWrapper) {
          trapFocus(
            cartDrawerWrapper,
            document.querySelector(".cart-item__name"),
          );
        }
        publish(PUB_SUB_EVENTS.cartUpdate, { source: "cart-items" });

        // Sync linked gift wrap service item quantity
        if (parseInt(quantity) === 0) {
          this.cleanupOrphanedGiftWrap(parsedState.items);
        } else {
          this.syncGiftWrapQuantity(parsedState.items, line, parseInt(quantity));
        }
      })
      .finally(() => {
        this.disableLoading(line);
      });
  }

  /**
   * Re-render cart sections from a parsed cart/change.js response that includes sections.
   * Uses the same DOM targeting as updateQuantity for consistency.
   */
  renderSectionsFromState(parsedState) {
    if (!parsedState || !parsedState.sections) return;

    this.getSectionsToRender().forEach((section) => {
      const sectionEl = document.getElementById(section.id);
      if (!sectionEl) return;
      const elementToReplace = sectionEl.querySelector(section.selector) || sectionEl;
      const sectionHtml = parsedState.sections[section.section];
      if (!sectionHtml) return;
      elementToReplace.innerHTML = this.getSectionInnerHTML(sectionHtml, section.selector) || '';
    });

    const cartDrawerWrapper = document.querySelector('cart-drawer');
    const cartFooter = document.getElementById('main-cart-footer');
    if (cartDrawerWrapper) cartDrawerWrapper.classList.toggle('is-empty', parsedState.item_count === 0);
    if (cartFooter) cartFooter.classList.toggle('is-empty', parsedState.item_count === 0);

    publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items' });
  }

  cleanupOrphanedGiftWrap(items) {
    if (!items || !items.length) return;

    // Collect product title + gift style combos for all gift-wrapped products
    const productGiftKeys = new Set();
    items.forEach(function(item) {
      if (item.handle !== 'gift-wrapping-service') {
        var giftStyle = item.properties && item.properties['Gift Style'];
        if (giftStyle) {
          productGiftKeys.add(item.product_title + '||' + giftStyle);
        }
      }
    });

    // Find gift wrap service items whose linked product+style is no longer in the cart
    var orphanedLines = [];
    items.forEach(function(item, index) {
      if (item.handle !== 'gift-wrapping-service') return;
      var linkedProduct = item.properties && item.properties['_linked_product'];
      var style = item.properties && item.properties['Style'];
      var key = (linkedProduct || '') + '||' + (style || '');
      if (!productGiftKeys.has(key)) {
        orphanedLines.push(index + 1);
      }
    });

    if (orphanedLines.length === 0) return;

    // Remove from highest line index first to avoid line number shifts
    orphanedLines.sort(function(a, b) { return b - a; });

    var self = this;
    var sectionNames = self.getSectionsToRender().map(function(s) { return s.section; }).filter(Boolean);

    // Chain sequential removals; include sections in the last request for re-rendering
    var chain = Promise.resolve();
    orphanedLines.forEach(function(line, i) {
      chain = chain.then(function() {
        var bodyData = { line: line, quantity: 0 };
        // Only the last removal needs section HTML for re-rendering
        if (i === orphanedLines.length - 1) {
          bodyData.sections = sectionNames;
          bodyData.sections_url = window.location.pathname;
        }
        return fetch(routes.cart_change_url, {
          ...fetchConfig(),
          body: JSON.stringify(bodyData),
        }).then(function(r) { return r.text(); })
          .then(function(t) { return JSON.parse(t); });
      });
    });

    chain
      .then(function(parsedState) {
        self.renderSectionsFromState(parsedState);
      })
      .catch(function(err) {
        console.error('Gift wrap orphan cleanup error:', err);
      })
      .finally(function() {
        // Ensure cart drawer refreshes even if section render had issues
        const cartDrawer = document.querySelector('cart-drawer');
        if (cartDrawer && typeof cartDrawer.refreshCartDrawer === 'function') {
          cartDrawer.refreshCartDrawer({});
        }
        // Re-publish cart update to trigger any listeners
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'gift-wrap-cleanup' });
        }
      });
  }

  syncGiftWrapQuantity(items, line, newQuantity) {
    if (!items || !items.length) return;

    // Find the item that was just updated (line is 1-indexed)
    const updatedItem = items[line - 1];
    if (!updatedItem) return;

    // Check if this item has a Gift Style property (meaning it has a linked gift wrap)
    const giftStyle = updatedItem.properties && updatedItem.properties['Gift Style'];
    if (!giftStyle) return;

    var self = this;
    var sectionNames = self.getSectionsToRender().map(function(s) { return s.section; }).filter(Boolean);

    // Find the linked gift wrap service item matching both product title and style
    items.forEach(function(item, index) {
      if (item.handle !== 'gift-wrapping-service') return;
      var linkedProduct = item.properties && item.properties['_linked_product'];
      var style = item.properties && item.properties['Style'];
      if (linkedProduct === updatedItem.product_title && style === giftStyle && item.quantity !== newQuantity) {
        fetch(routes.cart_change_url, {
          ...fetchConfig(),
          body: JSON.stringify({
            line: index + 1,
            quantity: newQuantity,
            sections: sectionNames,
            sections_url: window.location.pathname,
          }),
        }).then(function(r) { return r.text(); })
          .then(function(t) { return JSON.parse(t); })
          .then(function(parsedState) {
            self.renderSectionsFromState(parsedState);
          })
          .catch(function(err) {
            console.error('Gift wrap quantity sync error:', err);
          })
          .finally(function() {
            // Ensure cart drawer refreshes
            const cartDrawer = document.querySelector('cart-drawer');
            if (cartDrawer && typeof cartDrawer.refreshCartDrawer === 'function') {
              cartDrawer.refreshCartDrawer({});
            }
          });
      }
    });
  }

  updateLiveRegions(line, message) {
    const lineItemError =
      document.getElementById(`Line-item-error-${line}`) ||
      document.getElementById(`CartDrawer-LineItemError-${line}`);
    if (lineItemError) {
      if (message.length > 0) lineItemError.style.display = "flex";
      lineItemError.querySelector(".cart-item__error-text").innerHTML = message;
    }

    this.lineItemStatusElement.setAttribute("aria-hidden", true);

    const cartStatus =
      document.getElementById("cart-live-region-text") ||
      document.getElementById("CartDrawer-LiveRegionText");
    cartStatus.setAttribute("aria-hidden", false);

    setTimeout(() => {
      cartStatus.setAttribute("aria-hidden", true);
    }, 1000);
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser()
      .parseFromString(html, "text/html")
      ?.querySelector(selector)?.innerHTML;
  }

  enableLoading(line) {
    const mainCartItems =
      document.getElementById("main-cart-items") ||
      document.getElementById("CartDrawer-CartItems");
    mainCartItems.classList.add("cart__items--disabled");

    const cartItemElements = this.querySelectorAll(
      `#CartItem-${line} .loading-overlay`,
    );
    const cartDrawerItemElements = this.querySelectorAll(
      `#CartDrawer-Item-${line} .loading-overlay`,
    );

    [...cartItemElements, ...cartDrawerItemElements].forEach((overlay) =>
      overlay.classList.remove("hidden"),
    );

    document.activeElement.blur();
    this.lineItemStatusElement.setAttribute("aria-hidden", false);
  }

  disableLoading(line) {
    const mainCartItems =
      document.getElementById("main-cart-items") ||
      document.getElementById("CartDrawer-CartItems");
    mainCartItems.classList.remove("cart__items--disabled");

    const cartItemElements = this.querySelectorAll(
      `#CartItem-${line} .loading-overlay`,
    );
    const cartDrawerItemElements = this.querySelectorAll(
      `#CartDrawer-Item-${line} .loading-overlay`,
    );

    cartItemElements.forEach((overlay) => overlay.classList.add("hidden"));
    cartDrawerItemElements.forEach((overlay) =>
      overlay.classList.add("hidden"),
    );
  }
}

customElements.define("cart-items", CartItems);

if (!customElements.get("cart-note")) {
  customElements.define(
    "cart-note",
    class CartNote extends HTMLElement {
      constructor() {
        super();

        this.addEventListener(
          "change",
          debounce((event) => {
            const body = JSON.stringify({
              note: document.querySelector('[data-id="wt-cart-note"]').value,
            });
            fetch(`${routes.cart_update_url}`, {
              ...fetchConfig(),
              ...{ body },
            });
          }, ON_CHANGE_DEBOUNCE_TIMER),
        );
      }
    },
  );
}
